// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle, XCircle, HelpCircle, Search } from "lucide-react";
import { useUpcomingEventsForScorekeepers, useScorekeepers, useScorekeeperAvailability } from "@/hooks/useScorekeepersData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format, eachDayOfInterval, parseISO } from "date-fns";

const SKAvailabilityTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForScorekeepers();
  const { data: scorekeepers, isLoading: skLoading } = useScorekeepers("active");
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: availability } = useScorekeeperAvailability(selectedEventId);
  const [search, setSearch] = useState("");

  const selectedEvent = events?.find((e: any) => e.id === selectedEventId);

  const eventDates = useMemo(() => {
    if (!selectedEvent) return [];
    try { return eachDayOfInterval({ start: parseISO(selectedEvent.start_date), end: parseISO(selectedEvent.end_date) }); }
    catch { return []; }
  }, [selectedEvent]);

  const availMap = useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {};
    (availability || []).forEach((a: any) => {
      if (!map[a.scorekeeper_id]) map[a.scorekeeper_id] = {};
      map[a.scorekeeper_id][a.available_date] = a.is_available;
    });
    return map;
  }, [availability]);

  const filteredSKs = useMemo(() => {
    return (scorekeepers || []).filter((s: any) => {
      if (search) { const q = search.toLowerCase(); if (!s.name?.toLowerCase().includes(q) && !s.city?.toLowerCase().includes(q) && !s.preferred_city?.toLowerCase().includes(q)) return false; }
      return true;
    });
  }, [scorekeepers, search]);

  const dateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    eventDates.forEach(d => {
      const dateStr = format(d, "yyyy-MM-dd");
      counts[dateStr] = filteredSKs.filter((s: any) => availMap[s.id]?.[dateStr] === true).length;
    });
    return counts;
  }, [eventDates, filteredSKs, availMap]);

  const toggleAvailability = async (scorekeeperId: string, dateStr: string) => {
    if (!selectedEventId) return;
    const current = availMap[scorekeeperId]?.[dateStr];
    const newVal = current === undefined ? true : current === true ? false : true;
    const { error } = await supabase.from("manage_scorekeeper_availability").upsert(
      { scorekeeper_id: scorekeeperId, event_id: selectedEventId, available_date: dateStr, is_available: newVal },
      { onConflict: "scorekeeper_id,available_date" }
    );
    if (error) { toast.error("Failed to update"); return; }
    queryClient.invalidateQueries({ queryKey: ["scorekeeper-availability", selectedEventId] });
  };

  const renderCell = (skId: string, dateStr: string) => {
    const val = availMap[skId]?.[dateStr];
    if (val === true) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (val === false) return <XCircle className="h-5 w-5 text-red-500" />;
    return <HelpCircle className="h-5 w-5 text-gray-300" />;
  };

  if (evLoading || skLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[280px]">
          <Label>Select Event</Label>
          <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
            <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
            <SelectContent>{(events || []).map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name} ({format(parseISO(e.start_date), "MMM d")} - {format(parseISO(e.end_date), "MMM d")})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input placeholder="Filter by name/city..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-[220px]" /></div>
      </div>

      {selectedEventId && eventDates.length > 0 && (
        <>
          <div className="bg-muted/30 border rounded p-3 text-sm text-muted-foreground">
            Click cells to toggle: <CheckCircle className="h-4 w-4 text-green-500 inline" /> Available → <XCircle className="h-4 w-4 text-red-500 inline" /> Unavailable → <HelpCircle className="h-4 w-4 text-gray-300 inline" /> Not Set
          </div>

          <Card><CardContent className="p-0"><div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="sticky left-0 bg-background z-10 min-w-[180px]">Scorekeeper</TableHead>
                <TableHead className="sticky left-[180px] bg-background z-10">City</TableHead>
                {eventDates.map(d => <TableHead key={d.toISOString()} className="text-center min-w-[80px]"><div className="text-xs">{format(d, "EEE")}</div><div>{format(d, "MMM d")}</div></TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {filteredSKs.length === 0 ? (
                  <TableRow><TableCell colSpan={2 + eventDates.length} className="text-center py-8 text-muted-foreground">No scorekeepers found.</TableCell></TableRow>
                ) : filteredSKs.map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell className="sticky left-0 bg-background font-medium">{s.name}</TableCell>
                    <TableCell className="sticky left-[180px] bg-background text-sm text-muted-foreground">{s.preferred_city || s.city ? `${s.preferred_city || s.city}, ${s.state || ""}` : "—"}</TableCell>
                    {eventDates.map(d => {
                      const dateStr = format(d, "yyyy-MM-dd");
                      return <TableCell key={dateStr} className="text-center"><Button variant="ghost" size="sm" className="p-0 h-auto" onClick={() => toggleAvailability(s.id, dateStr)}>{renderCell(s.id, dateStr)}</Button></TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter><TableRow>
                <TableCell className="sticky left-0 bg-background font-bold">Available</TableCell>
                <TableCell className="sticky left-[180px] bg-background"></TableCell>
                {eventDates.map(d => { const dateStr = format(d, "yyyy-MM-dd"); return <TableCell key={dateStr} className="text-center font-bold">{dateCounts[dateStr] || 0}</TableCell>; })}
              </TableRow></TableFooter>
            </Table>
          </div></CardContent></Card>

          <div className="bg-muted/30 border rounded p-3 text-sm text-muted-foreground italic">
            📧 Feature coming: Bulk email scorekeepers to request availability for this event.
          </div>
        </>
      )}

      {selectedEventId && eventDates.length === 0 && (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No dates found for this event.</CardContent></Card>
      )}
    </div>
  );
};

export default SKAvailabilityTab;
